import { useState } from 'react';
import { Button, Tooltip } from '@mui/material';
import SupervisorAccount from '@mui/icons-material/SupervisorAccount';
import { useTranslation } from 'react-i18next';
import { useHasPermission } from '@/components/PermissionGate';
import { CommandResultSnackbar } from '@/components/CommandResultSnackbar';
import { getErrorMessage } from '@/lib/apiClient';
import { authService } from '@/services/auth/authService';
import { IMPERSONATE_PERMISSION } from '@/services/auth/impersonation';
import { useImpersonation } from '@/core/hooks/useImpersonation';
import type { ListViewExtraProps } from '@/config/listViewExtras';

/**
 * The user "Log in as" can act on: exactly one selected, still loaded, active, and not the caller.
 * These only avoid offering a click that can only fail — the server re-checks every impersonation
 * rule, and rejects an admin-role target with its own message (the list rows carry no roles).
 */
function pickImpersonationTarget(
  selectedIds: string[],
  selectedRows: Record<string, unknown>[],
  currentUserId: string | undefined,
): string | null {
  if (selectedIds.length !== 1) return null;
  const row = selectedRows.find((r) => String(r.id) === selectedIds[0]);
  if (!row || row.isActive !== true || selectedIds[0] === currentUserId) return null;
  return selectedIds[0];
}

/** Toolbar action on the Users grid: act as the selected user. */
export function LogInAsUserButton({ selectedIds, selectedRows }: ListViewExtraProps) {
  const { t } = useTranslation();
  const impersonate = useImpersonation();
  const permitted = useHasPermission(IMPERSONATE_PERMISSION);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Hidden entirely while impersonating — nested impersonation is forbidden (R3).
  if (!permitted || authService.isImpersonating()) return null;

  const target = pickImpersonationTarget(selectedIds, selectedRows, authService.getCurrentUser()?.sub);

  const handleClick = async () => {
    if (!target) return;
    setPending(true);
    setError(null);
    try {
      await impersonate(target);
    } catch (e: unknown) {
      setError(getErrorMessage(e) || t('identity.impersonateFailed'));
      setPending(false);
    }
  };

  return (
    <>
      {/* A disabled MUI Button swallows pointer events, so the tooltip needs a wrapper to hear them. */}
      <Tooltip title={target ? t('identity.impersonateTitle') : t('identity.logInAsHint')}>
        <span>
          <Button
            startIcon={<SupervisorAccount />}
            disabled={!target || pending}
            onClick={handleClick}
            sx={{ textTransform: 'none', color: 'text.secondary' }}
          >
            {t('identity.impersonate')}
          </Button>
        </span>
      </Tooltip>

      <CommandResultSnackbar
        result={error ? { severity: 'error', message: error } : null}
        onClose={() => setError(null)}
      />
    </>
  );
}
